<template>
	<div class="omapi-card" :class="cardClass" @click="$emit('click', $event)">
		<div
			v-show="isOpen || isMin"
			v-if="hasTitle"
			class="omapi-card-title capital"
			:class="titleClass"
			@click="handleCloseCard"
		>
			<slot name="title">{{ title }}</slot>
			<div v-if="isMinimizable && isOpen" class="omapi-card-icon omapi-card-icon__min-open">Minimize</div>
			<div v-else-if="isMinimizable && isMin" class="omapi-card-icon omapi-card-icon__min-closed">Open</div>
			<div v-else-if="isDismissable" class="omapi-card-icon omapi-card-icon__close">Dismiss</div>
		</div>
		<div v-show="isOpen" class="omapi-card-content" :class="contentClass">
			<!-- If there is no title, but we still need to be able to dismiss -->
			<div
				v-if="isDismissable && !hasTitle"
				class="omapi-card-icon omapi-card-icon__close"
				@click="handleCloseCard"
			>
				Dismiss
			</div>
			<slot></slot>
		</div>
		<div v-if="hasFooter" v-show="isOpen" class="omapi-card-footer" :class="footerClass">
			<slot name="footer"></slot>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Card',
	props: {
		type: {
			type: String,
			default() {
				return 'permanent';
			},
		},
		size: {
			type: [String, Number],
			default() {
				return 'full';
			},
		},
		defaultStatus: {
			type: String,
			default() {
				return 'open';
			},
		},
		title: {
			type: [String, Boolean],
			default() {
				return '';
			},
		},
		footer: {
			type: [String, Boolean],
			default() {
				return '';
			},
		},
		content: {
			type: String,
			default: '',
		},
		className: {
			type: String,
			default: '',
		},
		titleClass: {
			type: String,
			default: '',
		},
		contentClass: {
			type: String,
			default: '',
		},
		footerClass: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			status: null,
		};
	},
	computed: {
		currentStatus() {
			return null === this.status ? this.defaultStatus : this.status;
		},
		isOpen() {
			return 'open' === this.currentStatus;
		},
		isMin() {
			return 'min' === this.currentStatus;
		},
		isMinimizable() {
			return 'minimize' === this.type;
		},
		isDismissable() {
			return 'dismiss' === this.type;
		},
		hasTitle() {
			return !!this.$slots.title || '' !== this.title;
		},
		hasFooter() {
			return !!this.$slots.footer || '' !== this.footer;
		},
		cardClass() {
			let className = [];
			if (this.className) {
				className.push(this.className);
			}

			switch (this.type) {
				case 'minimize': {
					className.push('omapi-can-minimize');
					break;
				}
				case 'dismiss': {
					className.push('omapi-can-dismiss');
					break;
				}
			}

			switch (this.size) {
				case 2:
				case 'half': {
					className.push('omapi-card__half');
					break;
				}
				case 3:
				case 'third': {
					className.push('omapi-card__third');
					break;
				}
				case 'two-thirds':
				case 'third-2': {
					className.push('omapi-card__third-two');
					break;
				}
				case 4:
				case 'fourth': {
					className.push('omapi-card__fourth');
					break;
				}
				case 5:
				case 'fifth': {
					className.push('omapi-card__fifth');
					break;
				}
				case 6:
				case 'sixth': {
					className.push('omapi-card__sixth');
					break;
				}
			}

			if (!this.hasTitle) {
				className.push('omapi-card__notitle');
			}
			if (!this.hasFooter) {
				className.push('omapi-card__nofooter');
			}

			if (this.status) {
				className.push('omapi-card__' + this.status);
			}

			return className.join(' ');
		},
	},
	methods: {
		handleCloseCard() {
			switch (this.type) {
				case 'permanent': {
					return;
				}
				case 'dismiss': {
					// This will not persist state. If card should be permanently closed
					// That will need to be stored in state elsewhere.
					this.status = 'closed';
					this.$emit('dismiss', this.status);
					break;
				}
				case 'minimize': {
					this.status = this.isOpen ? 'min' : 'open';
					this.$emit('minimize');
					break;
				}
			}
		},
	},
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';
@import '../../assets/scss/components/cards';
</style>
